import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'
import { MessageSquare, Edit2, Trash2, Search, X } from 'lucide-react'
import ConfirmModal from '../ui/ConfirmModal'
import Modal from '../ui/Modal'

// Set to true to re-enable comment management in a later phase
const COMMENTS_ENABLED = false

function AdminCommentsList() {
  const [comments, setComments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [entriesCount, setEntriesCount] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [commentToDelete, setCommentToDelete] = useState(null)
  const [commentToEdit, setCommentToEdit] = useState(null)
  const [editedCommentText, setEditedCommentText] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const api = apiService()

  useEffect(() => {
    fetchComments()
  }, [currentPage, entriesCount, searchTerm])

  const fetchComments = async () => {
    try {
      setIsLoading(true)
      const params = {
        page: currentPage,
        limit: entriesCount,
        ...(searchTerm && { search: searchTerm })
      }
      const queryString = new URLSearchParams(params).toString()
      const response = await api.get(`admin/comments?${queryString}`)
      
      if (response.status === 200 && response.data.success) {
        setComments(response.data.comments || [])
        setTotalPages(response.data.pagination?.pages || 1)
      } else {
        toast.error(response.data?.message || 'Failed to fetch comments')
      }
    } catch (error) {
      console.error('Error fetching comments:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch comments'
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteClick = (comment) => {
    setCommentToDelete(comment)
    setDeleteModalOpen(true)
  }

  const handleEditClick = (comment) => {
    setCommentToEdit(comment)
    setEditedCommentText(comment.comment)
    setEditModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (commentToDelete) {
      try {
        const response = await api.del(`lessons/comments/${commentToDelete._id}`)
        if (response.status === 200) {
          toast.success('Comment deleted successfully')
          await fetchComments()
          setDeleteModalOpen(false)
          setCommentToDelete(null)
        }
      } catch (error) {
        console.error('Error deleting comment:', error)
        toast.error(error.response?.data?.message || 'Failed to delete comment')
      }
    }
  }

  const handleSaveEdit = async () => {
    if (!editedCommentText.trim()) {
      toast.error('Comment cannot be empty')
      return
    }

    try {
      setIsSaving(true)
      const response = await api.put(`admin/comments/${commentToEdit._id}`, {
        comment: editedCommentText.trim()
      })
      
      if (response.status === 200) {
        toast.success('Comment updated successfully')
        setEditModalOpen(false)
        setCommentToEdit(null)
        setEditedCommentText('')
        await fetchComments()
      }
    } catch (error) {
      console.error('Error updating comment:', error)
      toast.error(error.response?.data?.message || 'Failed to update comment')
    } finally {
      setIsSaving(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1) // Reset to first page on search
  }

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (!COMMENTS_ENABLED) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-2">
            <MessageSquare className="w-6 h-6" />
            Lesson Comments Management
          </h1>
          <p className="text-gray-600">Comment functionality will be available in a later phase.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-2">
          <MessageSquare className="w-6 h-6" />
          Lesson Comments Management
        </h1>
        <p className="text-gray-600">View, edit, and delete comments on all lessons</p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by comment text, student name, email, or lesson title..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE3C7D] focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Entries per page */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Show:</label>
          <select
            value={entriesCount}
            onChange={(e) => {
              setEntriesCount(Number(e.target.value))
              setCurrentPage(1)
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#DE3C7D]"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
        <div className="text-sm text-gray-600">
          Showing {comments.length} of {totalPages * entriesCount} comments
        </div>
      </div>

      {/* Comments Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#DE3C7D]"></div>
          <p className="mt-2 text-gray-600">Loading comments...</p>
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No comments found</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Lesson
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Comment
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {comments.map((comment) => (
                  <tr key={comment._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {comment.student_id?.first_name} {comment.student_id?.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {comment.student_id?.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">
                        {comment.lesson_id?.title || 'N/A'}
                      </div>
                      {comment.lesson_id?.course && (
                        <div className="text-xs text-gray-500">
                          Course: {comment.lesson_id.course}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 max-w-md">
                        {comment.comment}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(comment.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditClick(comment)}
                          className="text-[#DE3C7D] hover:text-[#c2356a] flex items-center gap-1"
                          title="Edit comment"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(comment)}
                          className="text-red-600 hover:text-red-800 flex items-center gap-1"
                          title="Delete comment"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 border rounded-lg ${
                          currentPage === page
                            ? 'bg-[#DE3C7D] text-white border-[#DE3C7D]'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  } else if (
                    page === currentPage - 3 ||
                    page === currentPage + 3
                  ) {
                    return <span key={page} className="px-2">...</span>
                  }
                  return null
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setCommentToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Comment"
        message={`Are you sure you want to delete this comment by ${commentToDelete?.student_id?.first_name} ${commentToDelete?.student_id?.last_name}?`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="red"
      />

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false)
          setCommentToEdit(null)
          setEditedCommentText('')
        }}
        title="Edit Comment"
      >
        <div className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Student: {commentToEdit?.student_id?.first_name} {commentToEdit?.student_id?.last_name}
            </label>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lesson: {commentToEdit?.lesson_id?.title || 'N/A'}
            </label>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comment
            </label>
            <textarea
              value={editedCommentText}
              onChange={(e) => setEditedCommentText(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE3C7D] focus:border-transparent"
              placeholder="Enter comment text..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setEditModalOpen(false)
                setCommentToEdit(null)
                setEditedCommentText('')
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={isSaving || !editedCommentText.trim()}
              className="px-4 py-2 bg-[#DE3C7D] text-white rounded-lg hover:bg-[#c2356a] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AdminCommentsList

